/**
 * Contratos do modulo de leads: lista enriquecida (`GET /api/leads`), resumo
 * do periodo (`GET /api/leads/summary`) e detalhe (`GET /api/leads/:id`).
 * O detalhe EXISTE no backend desde 25/09/2026 (src/api/leadDetail.ts): a
 * linha do tempo real traz fato, fila, ligacoes, WhatsApp enviado/falhou,
 * pagamento, estorno, venda assistida, falha e fim da regua -- os estagios
 * que o motor ainda nao mede (entregue, lido, clique, resposta, e-mail) nao
 * aparecem, e `rule.ladder` vem vazia. O resumo e os campos da lista alem de
 * `LeadListItem` ainda nao existem -- plano em docs/PLANO-PAINEL-COMPLETO.md.
 *
 * Decisao central: a linha do tempo e uma UNIAO DISCRIMINADA por `kind`, e
 * nao um evento generico com `payload: unknown`. Cada tipo de evento carrega
 * so os campos que fazem sentido para ele (uma ligacao tem gravacao; um
 * WhatsApp tem estagio; um pagamento tem credito e comissao), entao a tela
 * nao precisa adivinhar campo opcional e o tsc aponta o `switch` incompleto
 * quando o backend acrescentar um tipo novo.
 *
 * O WhatsApp aparece como VARIOS eventos (enviado, entregue, lido, clicou),
 * um por estagio, em vez de um evento com quatro datas: e assim que o motor
 * vai receber os callbacks da Evolution API, e e assim que a linha do tempo
 * mostra a ordem real das coisas.
 *
 * A lista (`LeadListRow`) ESTENDE o `LeadListItem` compartilhado em vez de
 * substitui-lo: o backend de hoje devolve so a base, e `api.ts` completa os
 * campos novos com `null`/derivados ate a rota crescer. Nada renomeia o que
 * ja existe.
 *
 * `SituacaoDoLead` e o vocabulario que o lojista entende ("pagou sozinho",
 * "esgotado", "com o comercial") derivado de `jobStatus` + linha do tempo. O
 * `jobStatus` cru continua na linha para quem precisa do estado do motor.
 *
 * Dinheiro em centavos inteiros, percentuais em bps, datas em ISO-8601 --
 * mesmas regras de `src/types.ts`. `commissionCents` vem pronto do servidor
 * (a regra de arredondamento mora em src/domain/money.ts).
 */

import type { CampaignStatus, CompanyId, FunnelKind, LeadId, LeadListItem, Page, Provider, RecoveryJobStatus, RecoveryTier } from '../../types';

/** Quem fez o contato creditado (ADR-001: ultimo contato antes do pagamento). */
export type ContactChannel = 'voice' | 'whatsapp' | 'email' | 'seller';

export interface TouchRef {
  readonly touchId: string;
  /** "T+30min", "T+1h", "T+24h" no carrinho; "D+15", "D+30" nos expirados. */
  readonly label: string;
  readonly channel: ContactChannel;
}

/** Um degrau da regua da empresa, como o servidor a conhece. Serve para desenhar o plano do lead. */
export interface DegrauDaRegua extends TouchRef {
  readonly order: number;
  /** Minutos depois do FATO (abandono ou vencimento). Nunca negativo: nao existe toque antes do fato. */
  readonly delayMinutes: number;
  readonly isLast: boolean;
}

export interface CreditedContact {
  readonly channel: ContactChannel;
  readonly touchLabel: string;
  /** Instante do contato (na voz, o INICIO da ligacao). */
  readonly at: string;
}

interface BaseEvent {
  readonly id: string;
  readonly at: string;
}

export interface WebhookReceivedEvent extends BaseEvent {
  readonly kind: 'webhook_received';
  readonly provider: Provider;
  /** "carrinho abandonado", "assinatura vencida"... ja em pt-BR. */
  readonly eventLabel: string;
  readonly amountCents: number;
  readonly externalId: string;
  /**
   * `ja_pago`: o carrinho chegou DEPOIS de um pagamento ja gravado do mesmo
   * pedido ou do mesmo lead (ordem de chegada). Conta no funil, mas nenhuma
   * regua foi agendada. Ausente no caso normal (src/api/leadDetail.ts, decisao 9).
   */
  readonly skipReason?: 'ja_pago';
}

export interface QueuedEvent extends BaseEvent {
  readonly kind: 'queued';
  readonly delayMinutes: number;
  readonly runAt: string;
}

/** Desfechos possiveis de uma ligacao; 'pending' e 'sent' sao de outros canais. */
export type VoiceCallStatus = Exclude<CampaignStatus, 'pending' | 'sent'>;

export interface VoiceCallEvent extends BaseEvent {
  readonly kind: 'voice_call';
  readonly attempt: number;
  /** RF08: teto de 2 por lead. */
  readonly maxAttempts: number;
  readonly status: VoiceCallStatus;
  readonly durationSeconds: number | null;
  readonly recordingUrl: string | null;
  /** Resumo curto do que aconteceu na chamada, quando a IA registrou. */
  readonly summary: string | null;
  readonly touchLabel: string;
}

export type WhatsAppStage = 'sent' | 'delivered' | 'read' | 'clicked' | 'failed';

export interface WhatsAppEvent extends BaseEvent {
  readonly kind: 'whatsapp';
  readonly touchLabel: string;
  readonly stage: WhatsAppStage;
  /** So em `clicked` (qual botao) e em `sent` (quais botoes foram). */
  readonly buttonLabel: string | null;
  readonly errorMessage: string | null;
}

export type EmailStage = 'sent' | 'opened' | 'clicked';

export interface EmailEvent extends BaseEvent {
  readonly kind: 'email';
  readonly touchLabel: string;
  readonly stage: EmailStage;
  readonly subject: string;
  /** `true` quando saiu como reserva porque o WhatsApp nao entregou. */
  readonly fallback: boolean;
}

export interface LeadReplyEvent extends BaseEvent {
  readonly kind: 'lead_reply';
  readonly channel: 'whatsapp' | 'email';
  readonly text: string;
}

export interface SellerTookOverEvent extends BaseEvent {
  readonly kind: 'seller_took_over';
  readonly sellerName: string;
}

export interface PaymentApprovedEvent extends BaseEvent {
  readonly kind: 'payment_approved';
  readonly provider: Provider;
  readonly amountCents: number;
  /** `null` = pagou antes de qualquer contato: aborta a regua e nao gera comissao. */
  readonly creditedTo: CreditedContact | null;
  readonly commissionCents: number;
  readonly feeBpsApplied: number;
  /**
   * `ja_creditado_por_venda_assistida`: o pedido ja rendeu comissao pela venda
   * assistida; este webhook para a regua, mas nao credita de novo (ADR-001 E5).
   * Ausente no caso normal.
   */
  readonly attribution?: 'ja_creditado_por_venda_assistida';
}

export interface ReversalEvent extends BaseEvent {
  readonly kind: 'reversal';
  readonly reason: 'refund' | 'chargeback';
  readonly amountCents: number;
  /** 0 quando nao havia conversao creditada. */
  readonly commissionRevertedCents: number;
}

/**
 * Venda assistida: fechada pela vendedora e registrada no painel (Pix manual,
 * link de outra plataforma), pelo POST /api/leads/:id/venda-assistida.
 * ADR-001 E5: a vendedora e "fechado por"; o credito continua no ultimo toque
 * do motor (`creditedTouch`), e `outcome` e o que a atribuicao respondeu.
 */
export interface SellerSaleEvent extends BaseEvent {
  readonly kind: 'seller_sale';
  /** Quem FECHOU a venda ("fechado por", `closed_by_user_id`). */
  readonly sellerName: string;
  /**
   * Quem LANCOU no painel, quando o servidor sabe (trilha da venda assistida
   * desde 25/09/2026). Ausente ou `null`: nao ha trilha, e a tela nao inventa.
   */
  readonly registeredByName?: string | null;
  readonly amountCents: number;
  readonly orderRef: string;
  /** `ja_creditado_por_webhook`: o mesmo pedido ja tinha conversao pelo webhook; sem segunda comissao. */
  readonly outcome: 'creditado' | 'creditado_revertido' | 'sem_contato' | 'ja_creditado_por_webhook';
  /** Rotulo do toque creditado ("WhatsApp T+1h"); `null` quando nao houve credito. */
  readonly creditedTouch: string | null;
  /** 0 quando nao ha comissao. */
  readonly commissionCents: number;
  readonly feeBpsApplied: number;
}

export interface RulePausedEvent extends BaseEvent {
  readonly kind: 'rule_paused';
  readonly paused: boolean;
}

export interface RuleRemovedEvent extends BaseEvent {
  readonly kind: 'rule_removed';
  readonly reason: string;
}

/** O motor desistiu do job (teto de resgates, canal fora do ar...). */
export interface JobFailedEvent extends BaseEvent {
  readonly kind: 'job_failed';
  readonly lastError: string;
}

/** A regua chegou ao fim sem o lead voltar (ou o lead pediu para sair). Nao e falha: e o desfecho normal de quem nao respondeu. */
export interface RuleEndedEvent extends BaseEvent {
  readonly kind: 'rule_ended';
  readonly reason: 'sem_resposta' | 'descadastrou';
  /** Quantos toques o lead recebeu ate aqui. */
  readonly touches: number;
}

export type TimelineEvent =
  | WebhookReceivedEvent
  | QueuedEvent
  | VoiceCallEvent
  | WhatsAppEvent
  | EmailEvent
  | LeadReplyEvent
  | SellerTookOverEvent
  | PaymentApprovedEvent
  | ReversalEvent
  | SellerSaleEvent
  | RulePausedEvent
  | RuleRemovedEvent
  | JobFailedEvent
  | RuleEndedEvent;

export type TimelineEventKind = TimelineEvent['kind'];

export interface LeadRuleState {
  readonly paused: boolean;
  readonly nextTouch: { readonly touch: TouchRef; readonly scheduledAt: string } | null;
  readonly voiceAttemptsUsed: number;
  readonly voiceAttemptsMax: number;
  /**
   * A regua da empresa para o funil deste lead, em ordem. Vazia quando o
   * backend ainda nao a devolve: a tela mostra so o proximo toque.
   */
  readonly ladder: readonly DegrauDaRegua[];
}

/** Linha de `recovery_conversions` (ADR-001), quando existe. */
export interface LeadConversion {
  readonly paidAt: string;
  readonly amountCents: number;
  readonly commissionCents: number;
  readonly feeBpsApplied: number;
  readonly creditedTo: CreditedContact;
  readonly reversed: boolean;
}

export interface LeadDetail {
  readonly id: LeadId;
  readonly companyId: CompanyId;
  /** Nome COMPLETO: o detalhe e a unica tela autorizada a mostra-lo (GUIA, secao 5). */
  readonly name: string | null;
  /** E.164; a tela mostra mascarado e so revela por acao explicita. */
  readonly phoneE164: string;
  readonly email: string | null;
  readonly provider: Provider;
  /** Id do pedido/checkout no provedor. */
  readonly externalId: string;
  readonly offerId: string | null;
  readonly productName: string | null;
  readonly amountCents: number;
  readonly funnel: FunnelKind;
  readonly jobStatus: RecoveryJobStatus;
  readonly tier: RecoveryTier;
  /** Instante do FATO: abandono do checkout (30 min sem pagar) ou vencimento do plano. Marco zero da regua. */
  readonly abandonedAt: string;
  /** Pagamento aprovado depois do abandono, creditado ou nao. */
  readonly recoveredAt: string | null;
  /** Carrinho a partir do percentil 80 dos valores abandonados da empresa (os mais caros). */
  readonly highValue: boolean;
  readonly rule: LeadRuleState;
  readonly conversion: LeadConversion | null;
  /** Em ordem cronologica. */
  readonly timeline: readonly TimelineEvent[];
}

// -----------------------------------------------------------------------------
// Lista (`GET /api/leads`) -- campos alem do `LeadListItem` compartilhado
// -----------------------------------------------------------------------------

/**
 * Situacao na linguagem do lojista. Derivada no servidor de `jobStatus` +
 * linha do tempo; `api.ts` deriva uma aproximacao quando o backend ainda
 * manda so o `jobStatus`.
 */
export type SituacaoDoLead =
  | 'na_fila'
  | 'em_recuperacao'
  | 'com_comercial'
  | 'recuperado'
  | 'pagou_sozinho'
  | 'esgotado'
  | 'falhou'
  | 'cancelado';

export const ORDEM_DAS_SITUACOES: readonly SituacaoDoLead[] = [
  'na_fila',
  'em_recuperacao',
  'com_comercial',
  'recuperado',
  'pagou_sozinho',
  'esgotado',
  'falhou',
  'cancelado',
];

/** O que aconteceu na ultima interacao com o lead (o estagio mais recente, nao o primeiro). */
export type DesfechoDoContato =
  | 'answered'
  | 'voicemail'
  | 'no_answer'
  | 'failed'
  | 'sent'
  | 'delivered'
  | 'read'
  | 'clicked'
  | 'opened'
  | 'replied'
  | 'took_over'
  | 'sale';

export interface UltimoContato {
  readonly channel: ContactChannel;
  readonly at: string;
  readonly outcome: DesfechoDoContato;
  readonly touchLabel: string;
}

export interface ProximoToque {
  readonly label: string;
  readonly channel: ContactChannel;
  readonly scheduledAt: string;
}

export interface LeadListRow extends LeadListItem {
  readonly funnel: FunnelKind;
  readonly offerId: string | null;
  readonly situacao: SituacaoDoLead;
  /** Contatos que chegaram ao telefone (ADR-001): ligacao atendida/caixa postal/sem resposta, mensagem enviada. */
  readonly contacts: number;
  readonly lastContact: UltimoContato | null;
  readonly nextTouch: ProximoToque | null;
  readonly highValue: boolean;
}

/** `nenhum` = ainda sem contato. */
export type CanalDoFiltro = ContactChannel | 'nenhum';

export type OrdenacaoDeLeads = 'abandonedAt' | 'amount' | 'situacao';
export type DirecaoDaOrdem = 'asc' | 'desc';

/** Tudo que a lista manda para o servidor. Filtros combinam entre si (E logico). */
export interface ConsultaDeLeads {
  readonly limit: number;
  readonly offset: number;
  readonly sort: OrdenacaoDeLeads;
  readonly dir: DirecaoDaOrdem;
  /** Nome, produto ou digitos do telefone. */
  readonly q: string;
  readonly situacao: SituacaoDoLead | null;
  readonly offerId: string | null;
  readonly canal: CanalDoFiltro | null;
  readonly funnel: FunnelKind | null;
  readonly altoValor: boolean;
}

/** Contagens por dimensao, sobre TODO o periodo (nao so a pagina), para os chips. */
export interface FacetasDeLeads {
  readonly porSituacao: Readonly<Record<SituacaoDoLead, number>>;
  readonly porCanal: Readonly<Record<CanalDoFiltro, number>>;
  readonly porFunil: Readonly<Record<FunnelKind, number>>;
  readonly porOferta: readonly { readonly offerId: string; readonly name: string; readonly count: number }[];
  readonly altoValor: number;
  /** `true` quando contadas so sobre os itens carregados (backend ainda sem facetas). */
  readonly parciais: boolean;
}

export interface PaginaDeLeads extends Page<LeadListRow> {
  readonly facets: FacetasDeLeads;
  /** Limiar de "alto valor" da empresa: percentil 80 dos valores abandonados (empates no limiar entram). */
  readonly highValueThresholdCents: number;
}

// -----------------------------------------------------------------------------
// Resumo do periodo (`GET /api/leads/summary`)
// -----------------------------------------------------------------------------

export interface ResumoAnterior {
  readonly abandoned: number;
  readonly abandonedAmountCents: number;
  readonly recovered: number;
  readonly recoveredAmountCents: number;
  readonly exhausted: number;
}

export interface ResumoDeLeads {
  readonly companyId: CompanyId;
  readonly periodStart: string;
  readonly periodEnd: string;
  /** Sairam do checkout (ou o plano venceu) na janela. */
  readonly abandoned: number;
  readonly abandonedAmountCents: number;
  readonly recovered: number;
  readonly recoveredAmountCents: number;
  readonly commissionCents: number;
  /** Retrato de agora: na fila + em recuperacao, entre os que entraram na janela. */
  readonly inRule: number;
  readonly inRuleAmountCents: number;
  readonly withSeller: number;
  /** Esgotaram a regua ou o motor falhou, sem voltar. */
  readonly exhausted: number;
  readonly exhaustedAmountCents: number;
  /** Alto valor ainda sem desfecho (nem pagou, nem cancelou). */
  readonly highValueOpen: number;
  readonly highValueOpenAmountCents: number;
  readonly highValueThresholdCents: number;
  /** Alto valor que esgotou a regua (ou o motor falhou) sem voltar. */
  readonly highValueExhausted: number;
  readonly highValueExhaustedAmountCents: number;
  /** Com a vendedora ha mais de 1 dia; `oldestSellerContactAt` e o contato mais antigo entre eles. */
  readonly withSellerOver1d: number;
  readonly withSellerOver1dAmountCents: number;
  readonly oldestSellerContactAt: string | null;
  /** Reguas encerradas por falha do motor (canal fora do ar). */
  readonly failed: number;
  /** Na fila com o primeiro toque nos proximos 30 minutos (retrato). */
  readonly dueSoon: number;
  readonly dueSoonAmountCents: number;
  /** Oferta com mais abandonos e nenhuma recuperacao (minimo 6), se houver. */
  readonly offerWithoutRecovery: { readonly offerId: string; readonly name: string; readonly abandoned: number } | null;
  /** Janela imediatamente anterior, do mesmo tamanho. `null` em "Desde o inicio". */
  readonly previous: ResumoAnterior | null;
}
